import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChevronDownIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import { fetchImportRules, updateImportRule, deleteImportRule, fetchBudgets, ImportRule, Budget } from '../api/budgets';
import LoadingSpinner from './LoadingSpinner';
import ConfirmDialog from './ConfirmDialog';

interface ImportRulesOverlayProps {
  onClose: () => void;
}

export function ImportRulesOverlay({ onClose }: ImportRulesOverlayProps) {
  const [importRules, setImportRules] = useState<ImportRule[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form state for editing
  const [ruleNoteMatchType, setRuleNoteMatchType] = useState<'Equals' | 'Includes'>('Equals');
  const [ruleNoteMatchValue, setRuleNoteMatchValue] = useState('');
  const [ruleAmountMatch, setRuleAmountMatch] = useState('');
  const [ruleUpdatedNote, setRuleUpdatedNote] = useState('');
  const [ruleBudget, setRuleBudget] = useState('');
  const [ruleSkip, setRuleSkip] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rules, budgetsData] = await Promise.all([
        fetchImportRules(),
        fetchBudgets()
      ]);
      setImportRules(rules);
      setBudgets(budgetsData);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load import rules');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRule = (rule: ImportRule) => {
    setEditingRuleId(rule._id);
    setRuleNoteMatchType(rule.noteMatchType);
    setRuleNoteMatchValue(rule.noteMatchValue);
    setRuleAmountMatch(rule.amountMatch !== null && rule.amountMatch !== undefined ? rule.amountMatch.toString() : '');
    setRuleUpdatedNote(rule.updatedNote);
    setRuleBudget(rule.skip ? '__skip__' : (rule.budget ? (typeof rule.budget === 'string' ? rule.budget : rule.budget._id) : ''));
    setRuleSkip(rule.skip || false);
  };

  const handleCancelEdit = () => {
    setEditingRuleId(null);
    setRuleNoteMatchType('Equals');
    setRuleNoteMatchValue('');
    setRuleAmountMatch('');
    setRuleUpdatedNote('');
    setRuleBudget('');
    setRuleSkip(false);
  };

  const handleSaveRule = async () => {
    if (!ruleNoteMatchValue.trim() || !ruleUpdatedNote.trim() || (!ruleBudget && ruleBudget !== '__skip__')) {
      return;
    }

    if (!editingRuleId) return;

    try {
      setIsSaving(true);
      const isSkip = ruleBudget === '__skip__';

      await updateImportRule(editingRuleId, {
        noteMatchType: ruleNoteMatchType,
        noteMatchValue: ruleNoteMatchValue.trim(),
        amountMatch: ruleAmountMatch ? parseFloat(ruleAmountMatch) : null,
        updatedNote: ruleUpdatedNote.trim(),
        budget: isSkip ? undefined : ruleBudget,
        skip: isSkip,
      });

      await loadData();
      handleCancelEdit();
    } catch (error) {
      console.error('Error updating rule:', error);
      alert(`Failed to update rule: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (ruleId: string) => {
    setRuleToDelete(ruleId);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!ruleToDelete) return;

    try {
      setIsDeleting(true);
      await deleteImportRule(ruleToDelete);
      await loadData();
      setShowDeleteDialog(false);
      setRuleToDelete(null);
    } catch (error) {
      console.error('Error deleting rule:', error);
      alert(`Failed to delete rule: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const getBudgetName = (rule: ImportRule): string => {
    if (rule.skip) {
      return 'Skip Transaction';
    }
    if (!rule.budget) {
      return 'No Budget';
    }
    if (typeof rule.budget === 'string') {
      const budget = budgets.find(b => b._id === rule.budget);
      return budget ? budget.name : 'Unknown Budget';
    }
    return rule.budget.name;
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-white flex flex-col w-full max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between py-4 px-8 border-b border-gray-200">
          <span className="text-lg font-semibold text-gray-900">Import Rules</span>
          <button
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors ml-4"
            onClick={onClose}
            aria-label="Close"
          >
            <XMarkIcon className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner />
            </div>
          ) : importRules.length === 0 ? (
            <div className="text-center text-gray-400 py-12">
              No import rules found. Create rules when importing transactions.
            </div>
          ) : (
            <div className="space-y-4">
              {importRules.map((rule) => (
                <div key={rule._id} className="bg-white rounded-lg border p-4 shadow-sm">
                  {editingRuleId === rule._id ? (
                    // Edit Mode
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Note Matching
                        </label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <select
                              value={ruleNoteMatchType}
                              onChange={(e) => setRuleNoteMatchType(e.target.value as 'Equals' | 'Includes')}
                              className="w-full appearance-none bg-gray-200 rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none border-0"
                              style={{
                                backgroundImage: 'none',
                                paddingRight: '2.5rem',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                              }}
                            >
                              <option value="Equals">Equals</option>
                              <option value="Includes">Includes</option>
                            </select>
                            <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
                          </div>
                          <input
                            type="text"
                            value={ruleNoteMatchValue}
                            onChange={(e) => setRuleNoteMatchValue(e.target.value)}
                            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Note value..."
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Amount (Optional)
                        </label>
                        <input
                          type="text"
                          value={ruleAmountMatch}
                          onChange={(e) => setRuleAmountMatch(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Leave blank to match any amount"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Updated Note
                        </label>
                        <input
                          type="text"
                          value={ruleUpdatedNote}
                          onChange={(e) => setRuleUpdatedNote(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Updated note..."
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Budget
                        </label>
                        <div className="relative">
                          <select
                            value={ruleBudget === '__skip__' ? '__skip__' : ruleBudget}
                            onChange={(e) => setRuleBudget(e.target.value)}
                            className="w-full appearance-none bg-gray-200 rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-800 focus:outline-none border-0"
                            style={{
                              backgroundImage: 'none',
                              paddingRight: '2.5rem',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                            }}
                            required
                          >
                            <option value="">Select budget...</option>
                            <option value="__skip__" className="text-red-600">Skip Transaction</option>
                            {budgets.map((budget) => (
                              <option key={budget._id} value={budget._id}>
                                {budget.name}
                              </option>
                            ))}
                          </select>
                          <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
                        </div>
                      </div>

                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={handleCancelEdit}
                          className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveRule}
                          disabled={!ruleNoteMatchValue.trim() || !ruleUpdatedNote.trim() || (!ruleBudget && ruleBudget !== '__skip__') || isSaving}
                          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                            ruleNoteMatchValue.trim() && ruleUpdatedNote.trim() && (ruleBudget || ruleBudget === '__skip__') && !isSaving
                              ? 'bg-[#22C55E] hover:bg-[#16A34A] text-white'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          {isSaving ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500">Note:</span>
                            <span className="text-sm font-semibold text-gray-900">{rule.noteMatchType}</span>
                            <span className="text-sm text-gray-700">"{rule.noteMatchValue}"</span>
                          </div>
                          {rule.amountMatch !== null && rule.amountMatch !== undefined && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-500">Amount:</span>
                              <span className="text-sm text-gray-700">${rule.amountMatch.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500">Updated Note:</span>
                            <span className="text-sm text-gray-700">{rule.updatedNote}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500">Budget:</span>
                            <span className={`text-sm font-medium ${rule.skip ? 'text-red-600' : 'text-gray-700'}`}>
                              {getBudgetName(rule)}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleEditRule(rule)}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                            title="Edit Rule"
                          >
                            <PencilIcon className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(rule._id)}
                            className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                            title="Delete Rule"
                          >
                            <TrashIcon className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        title="Delete Import Rule"
        message="Are you sure you want to delete this import rule? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setRuleToDelete(null);
        }}
        type="danger"
      />
    </>
  );
}

